import * as React from 'react'
import { CustomerDataMigrationViewModel } from '../../../api'
import { Formik } from 'formik'
import {
  Form,
  Box,
  Typography,
  ConfirmationModal,
  Loader,
  TypographyProps,
} from 'cs-kevlar-react'
import { EditModalActionBar } from '../../EditModalActionBar'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

interface LicenseMigrationInstructionsProps {
  customerMigrationStatus: CustomerDataMigrationViewModel
  onLicenseMigrationConfirmed: (values: any, action: any) => void
  refresh: () => void
  children: any
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      padding: theme.spacing(8),
    },
    scrollable: {
      overflowY: 'auto',
    },
    topMargin: {
      marginTop: theme.spacing(10),
    },
  }),
)

const DEFAULT_VALUES = {}

const LoaderTextProps: TypographyProps = {
  variant: 'body1',
}

export const LicenseMigrationInstructions: React.FunctionComponent<LicenseMigrationInstructionsProps> = ({
  customerMigrationStatus,
  onLicenseMigrationConfirmed,
  refresh,
  children,
}) => {
  const classes = useStyles({})
  const [
    isConfirmingLicenseMigration,
    setIsConfirmingLicenseMigration,
  ] = React.useState<boolean>(false)

  const formSubmitAction = customerMigrationStatus.hasPushedExpectedDocuments
    ? onLicenseMigrationConfirmed
    : (values: any, action: any) => {
        setIsConfirmingLicenseMigration(true)
        action.setSubmitting(false)
      }

  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (!customerMigrationStatus.hasPushedExpectedDocuments) {
      interval = setInterval(() => {
        refresh()
      }, 5000)
    }
    return () => {
      if (interval !== null) {
        clearInterval(interval)
      }
    }
  }, [customerMigrationStatus.hasPushedExpectedDocuments])
  return (
    <Formik initialValues={DEFAULT_VALUES} onSubmit={formSubmitAction}>
      {formikProps => (
        <Form onSubmit={formikProps.handleSubmit} className={classes.root}>
          <Box className={classes.scrollable}>
            {customerMigrationStatus.hasPushedExpectedDocuments && (
              <Box className={classes.content}>
                <Typography>
                  All expected documents have been pushed. Continue license
                  migration below.
                </Typography>
              </Box>
            )}
            {!customerMigrationStatus.hasPushedExpectedDocuments && (
              <Box className={classes.content}>
                <Loader
                  textProps={LoaderTextProps}
                  disableVerticalGutters
                  size="sm"
                  text="Waiting for connector to provide agency, user, and device information.  If new connector has not been started please start it now."
                />
              </Box>
            )}
            {children}
          </Box>
          <Box className={classes.spacer}></Box>
          <EditModalActionBar
            canDelete={false}
            canCancel={false}
            formikProps={formikProps}
            saveText={
              customerMigrationStatus.hasPushedExpectedDocuments
                ? 'Migrate'
                : 'Migrate anyway'
            }
          />
          <ConfirmationModal
            onCancel={() => setIsConfirmingLicenseMigration(false)}
            showCancelButton={false}
            onConfirm={onLicenseMigrationConfirmed as () => void}
            confirmButtonColor="secondary"
            confirmText="Confirm"
            open={isConfirmingLicenseMigration}
            title="Migrate Licenses"
            message={
              <React.Fragment>
                <Typography>
                  Are you sure you want to migrate licenses without all the
                  expected data?
                </Typography>
                <Typography className={classes.topMargin}>
                  Any expected users and agencies will not be migrated and will
                  require manual intervention to re-add agencies, licenses, and
                  reactivate devices.
                </Typography>
              </React.Fragment>
            }
          />
        </Form>
      )}
    </Formik>
  )
}
